// kHtmlSafeCore - an ALLOW-LIST cleaner for untrusted HTML + inline SVG. Pure TypeScript, no DOM,
// so it runs in the browser, in Node and in an Azure Function alike.
//
// WHY IT EXISTS (2026-09-26, WACommander). AI answers in the owner's panel may carry tables and SVG
// charts. The model's output can be steered by hostile text inside a contact's messages, and the
// panel runs beside a credential that can SEND WhatsApp messages as the owner - so any script
// execution is an attacker sending messages. The design is: parse (kHtmlParserCore) -> keep only
// what an explicit allow-list names -> the consumer builds elements itself from the returned tree
// (never innerHTML of model text). `ToHtml` re-serialises the survivors (kHtmlDomWriterCore) for a
// server that forwards cleaned markup to such a consumer.
//
// THE RULES, in the order they are applied:
//   1. Whole-source limits (MaxSourceChars, then a cheap pre-scan bounding tag count and nesting so
//      the recursive parser never sees a pathological input), then the parsed tree's real node
//      count and depth. Any breach refuses the WHOLE answer - never a truncated half-table.
//   2. Tags: a fixed HARD-DENY list no policy can re-admit (script, style, foreignObject, use, a,
//      animate/set, iframe, ...), then the policy's HtmlTags / SvgTags. A dropped element keeps
//      NOTHING of its subtree. SVG tags live only under an <svg>; an <svg> only in HTML context
//      (never nested); HTML never inside an <svg>; per-parent SVG structure (stop only in a
//      gradient, tspan only in text, text only in text/tspan/title/desc).
//   3. Attributes: a fixed HARD-DENY on names (on*, href, src, style, any name with ':' ...), then
//      the policy's per-namespace table, then a HARD-DENY on values (javascript:/data:/vbscript:
//      after entity decoding and whitespace/control stripping, any url( other than url(#id)), then
//      a strict per-kind value check (kHtmlSafeValueCore).
//   4. Every surviving name is re-written in its CANONICAL spelling from the allow-list, so what a
//      consumer or the writer emits never depends on how the source spelled it.
//
// A SECOND CONSUMER changes only the policy: `new kHtmlSafePolicy()` allows NOTHING (safe default);
// fill HtmlTags / SvgTags / HtmlAttributes / SvgAttributes / AttributeOnly / Classes, or start from
// `kHtmlSafeCore.AiAnswerPolicy(Classes)` and trim. The hard-deny lists are deliberately NOT policy.

import { getCoreUtils } from "./kCoreHandler";
import { kHtmlParserCore } from "./kHtmlParserCore";
import type { IkDomElement, IkDomDocument } from "./kHtmlParserCore";
import { kHtmlDomWriterCore } from "./kHtmlDomWriterCore";
import { kHtmlSafeWalk } from "./kHtmlSafeWalk";
import { kHtmlSafeAllowList } from "./kHtmlSafeAllowList";
// Re-exported so every import from "./kHtmlSafeCore" keeps working after the 2026-10-19 split.
export { kHtmlSafeValueCore } from "./kHtmlSafeValueCore";
export { kHtmlSafeWalk } from "./kHtmlSafeWalk";
// The allow-list data lives in the leaf kHtmlSafeAllowList.ts since 2026-10-22 (re-exported unchanged).
export { kHtmlSafeAllowList } from "./kHtmlSafeAllowList";

export class kHtmlSafePolicy {
    public MaxSourceChars: number = 60000;
    public MaxNodes: number = 2000;
    public MaxDepth: number = 32;
    /** Allowed class tokens; any other token is removed from a class attribute (empty list = no class attribute at all). */
    public Classes: string[] = [];
    /** An <svg> without a valid viewBox is dropped whole. */
    public RequireSvgViewBox: boolean = true;
    /** HTML tags allowed (lower case). Empty = none. */
    public HtmlTags: string[] = [];
    /** SVG tags allowed, in their canonical spelling ("linearGradient"). Empty = none. */
    public SvgTags: string[] = [];
    /** HTML attribute name -> value kind (see kHtmlSafeValueCore.Check). */
    public HtmlAttributes: Record<string, string> = {};
    /** SVG attribute name (canonical spelling, "viewBox") -> value kind. */
    public SvgAttributes: Record<string, string> = {};
    /** Attribute name -> the only tags it may appear on. An attribute not named here may appear on any allowed tag. */
    public AttributeOnly: Record<string, string[]> = {};
    /** Prefixed to every surviving id and url(#id) reference, so an answer can never clobber a page id or a window global. */
    public IdPrefix: string = "";
    /** Longest free-text attribute value (title, aria-label). */
    public MaxTextChars: number = 200;
}

export class kHtmlSafeResult {
    /** A container element (tagName "DIV") whose children are what survived; null when nothing survived or the source was refused whole. */
    public Root: IkDomElement | null = null;
    /** "" | "too-large" | "too-many-nodes" | "too-deep" | "empty" */
    public Refused: string = "";
    /** Elements + attributes dropped by the allow-list (for tests/logging counts). */
    public Dropped: number = 0;
}

export class kHtmlSafeCore {
    // DOCUMENTED EXCEPTION: alias of kHtmlSafeAllowList.SvgNs (the same value; the leaf holds it since 2026-10-22).
    public static readonly SvgNs: string = kHtmlSafeAllowList.SvgNs;

    /** The policy for AI chat answers: tables, simple text, static SVG charts. */
    public static AiAnswerPolicy = (Classes: string[], IdPrefix: string = "wac-ai-"): kHtmlSafePolicy => {
        if (getCoreUtils().KErrorMode) { return new kHtmlSafePolicy(); }
        try {
            const Policy: kHtmlSafePolicy = new kHtmlSafePolicy();
            Policy.Classes = Classes.slice();
            Policy.HtmlTags = kHtmlSafeAllowList.AiHtmlTags.slice();
            Policy.SvgTags = kHtmlSafeAllowList.AiSvgTags.slice();
            Policy.HtmlAttributes = Object.assign({}, kHtmlSafeAllowList.AiHtmlAttributes);
            Policy.SvgAttributes = Object.assign({}, kHtmlSafeAllowList.AiSvgAttributes);
            Policy.AttributeOnly = Object.assign({}, kHtmlSafeAllowList.AiAttributeOnly);
            Policy.IdPrefix = IdPrefix;
            return Policy;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeCore.AiAnswerPolicy");
            return new kHtmlSafePolicy();
        }
    };

    /** The allowed part of Source as a fresh tree under a DIV, or a refusal. Never throws on hostile input. */
    public static Clean = (Source: string, Policy: kHtmlSafePolicy): kHtmlSafeResult => {
        if (getCoreUtils().KErrorMode) { return new kHtmlSafeResult(); }
        try {
            const Result: kHtmlSafeResult = new kHtmlSafeResult();
            const Text: string = String(Source || "");
            Result.Refused = kHtmlSafeCore.PreScan(Text, Policy);
            if (Result.Refused !== "") { return Result; }
            const Doc: IkDomDocument = kHtmlParserCore.ParseFromString(Text);
            const Walk: kHtmlSafeWalk = new kHtmlSafeWalk(Policy);
            Walk.Measure(Doc.body, 0);
            if (Walk.Nodes > Policy.MaxNodes) { Result.Refused = "too-many-nodes"; return Result; }
            if (Walk.Deepest > Policy.MaxDepth) { Result.Refused = "too-deep"; return Result; }
            const Root: IkDomElement = kHtmlParserCore.CreateElement("div");
            Walk.CopyChildren(Doc.body, Root, "html");
            Result.Dropped = Walk.Dropped;
            if (kHtmlSafeCore.IsBlank(Root) === true) { Result.Refused = "empty"; return Result; }
            Result.Root = Root;
            return Result;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeCore.Clean");
            return new kHtmlSafeResult();
        }
    };

    /**
     * Refusals decided on the raw text, BEFORE the parser runs: the parser recurses once per nesting
     * level and rescans per element, so a pathological answer must never reach it. The tag count and
     * nesting here are an estimate (unclosed tags count as open), so nesting is bounded at four times
     * MaxDepth; the parsed tree's real counts decide the rest.
     */
    public static PreScan = (Text: string, Policy: kHtmlSafePolicy): string => {
        if (getCoreUtils().KErrorMode) { return "empty"; }
        try {
            if (Text.length > Policy.MaxSourceChars) { return "too-large"; }
            if (Text.trim() === "") { return "empty"; }
            const Tag: RegExp = /<(\/?)([A-Za-z][A-Za-z0-9]*)[^<>]*?(\/?)>/g;
            let Tags: number = 0;
            let Depth: number = 0;
            let Match: RegExpExecArray | null = Tag.exec(Text);
            while (Match !== null) {
                Tags = Tags + 1;
                if (Tags > Policy.MaxNodes) { return "too-many-nodes"; }
                if (Match[1] === "/") { Depth = Math.max(0, Depth - 1); }
                else if (Match[3] !== "/" && kHtmlParserCore.IsVoidTag(Match[2]) === false) { Depth = Depth + 1; }
                if (Depth > Policy.MaxDepth * 4) { return "too-deep"; }
                Match = Tag.exec(Text);
            }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeCore.PreScan");
            return "empty";
        }
    };

    /** True when nothing but whitespace survived. */
    private static IsBlank = (Root: IkDomElement): boolean => {
        if (getCoreUtils().KErrorMode) { return true; }
        try {
            for (const Kid of Root.childNodes) {
                if (Kid.nodeType === 1) { return false; }
                if ((Kid.nodeValue || "").trim() !== "") { return false; }
            }
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeCore.IsBlank");
            return true;
        }
    };

    /** The survivors serialised (kHtmlDomWriterCore), "" when Root is null. Used by the service to forward cleaned markup. */
    public static ToHtml = (Result: kHtmlSafeResult): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Result.Root === null) { return ""; }
            return kHtmlDomWriterCore.InnerHtml(Result.Root);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeCore.ToHtml");
            return "";
        }
    };

    /** The element's real tag name, case kept for SVG ("linearGradient"), lower case for HTML. */
    public static TagOf = (El: IkDomElement): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            return kHtmlDomWriterCore.TagNameOf(El, kHtmlSafeCore.InSvg(El) === true);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeCore.TagOf");
            return "";
        }
    };

    /** True when El is <svg> or inside one. */
    public static InSvg = (El: IkDomElement): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            let Node: IkDomElement | null = El;
            while (Node !== null) {
                if (Node.tagName === "SVG") { return true; }
                Node = Node.parentNode;
            }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeCore.InSvg");
            return false;
        }
    };

    /** Attribute names as written (case kept), in source order, of an element that came out of Clean. */
    public static AttributeNamesOf = (El: IkDomElement): string[] => {
        if (getCoreUtils().KErrorMode) { return []; }
        try {
            const Names: string[] = [];
            for (const Key of Object.keys(El.attributes)) {
                let Name: string = Key;
                if (El.attributeNames !== undefined && El.attributeNames[Key] !== undefined) { Name = El.attributeNames[Key]; }
                Names.push(Name);
            }
            return Names;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeCore.AttributeNamesOf");
            return [];
        }
    };
}
